/**
 * HMRC SDLT return form — rebuilt to mirror HMRC's Stamp Taxes Online
 * filing screens exactly (section order, official numbering and wording).
 *
 * HMRC Online nav order (matched below):
 *   1. About the purchaser(s)
 *   2. About the vendor(s)
 *   3. About the land
 *   4. About UK residency
 *   5. About the transaction (+ additional details)
 *   6. Consideration & value
 *   7. Reliefs & exemptions
 *   8. New lease details (leasehold only)
 *   9. Filing agent details
 *  10. Tax calculation & payment
 *  11. Declaration & signature
 *
 * All existing metadata field keys are preserved so the live Stamp Taxes
 * Online XML adapter continues to map correctly; new HMRC fields are added
 * additively.
 */
import { TextField, NumberField, DateField, SelectField, YesNoField, TextAreaField, PanelGrid, ComboboxField } from "./FormPrimitives";
import { LA_OPTIONS } from "./laCodes";
import { Button } from "@/components/ui/button";
import { Plus, Trash, Calculator } from "@phosphor-icons/react";
import { useState, useEffect } from "react";
import API from "@/lib/api";
import SdltCalculatorDialog from "../../SdltCalculatorDialog";

// ---- Static option lists -------------------------------------------------

// HMRC (1.2) Description of transaction
const TRANSACTION_DESCRIPTIONS = [
  { value: "F", label: "F — Conveyance / transfer" },
  { value: "L", label: "L — Grant of a lease" },
  { value: "A", label: "A — Assignment of a lease" },
  { value: "S", label: "S — Surrender of a lease" },
  { value: "O", label: "O — Other" },
];

// HMRC (1.3) Interest transferred or created — official SDLT1 box 3 codes.
// These describe the NATURE of the interest only; none of them require the
// lease-detail boxes (16-20). Those are driven solely by box 2 = L.
const INTEREST_TRANSFERRED = [
  { value: "FP", label: "FP — Freehold with vacant possession" },
  { value: "FG", label: "FG — Freehold subject to lease(s) at a ground/nominal rent" },
  { value: "FT", label: "FT — Freehold subject to a lease or tenancy (occupier)" },
  { value: "LP", label: "LP — Leasehold with vacant possession / lease to an occupier" },
  { value: "LG", label: "LG — Long leasehold at a ground/nominal rent (vacant possession)" },
  { value: "LT", label: "LT — Leasehold subject to leases to an occupier" },
  { value: "OT", label: "OT — Other" },
];

const TRANSACTION_TYPES = [
  { value: "freehold", label: "F — Freehold purchase" },
  { value: "leasehold_assigned", label: "L — Assignment of existing lease" },
  { value: "leasehold_new", label: "A — Grant of a new lease" },
  { value: "transfer_of_part", label: "P — Transfer of part" },
  { value: "other", label: "O — Other" },
];

const TENURE_TYPES = [
  { value: "freehold", label: "Freehold" },
  { value: "leasehold", label: "Leasehold" },
  { value: "commonhold", label: "Commonhold" },
];

// HMRC (1.1) Type of property
const PROPERTY_TYPES = [
  { value: "01", label: "01 — Residential" },
  { value: "02", label: "02 — Mixed use" },
  { value: "03", label: "03 — Non-residential" },
];

// HMRC (1.32) area unit for agricultural / development land
const AREA_UNITS = [
  { value: "na", label: "Not applicable" },
  { value: "hectares", label: "Hectares" },
  { value: "sqm", label: "Square metres" },
  { value: "acres", label: "Acres" },
  { value: "sqft", label: "Square feet" },
];

const PROPERTY_SUBTYPES = [
  { value: "standard_residential", label: "Standard residential dwelling" },
  { value: "new_build", label: "New build" },
  { value: "holiday_home", label: "Holiday home" },
  { value: "granny_flat", label: "Granny flat / annexe" },
  { value: "houseboat", label: "Houseboat" },
  { value: "caravan", label: "Caravan or mobile home" },
  { value: "student", label: "Student accommodation" },
  { value: "nursing_home", label: "Nursing / care home" },
  { value: "hotel", label: "Hotel" },
  { value: "pub", label: "Pub / restaurant" },
  { value: "shop", label: "Shop / retail" },
  { value: "office", label: "Office" },
  { value: "industrial", label: "Industrial premises" },
  { value: "agricultural", label: "Agricultural land" },
  { value: "woodland", label: "Woodland" },
  { value: "mixed", label: "Mixed-use (commercial + residential)" },
];

// HMRC Q70 — form-of-consideration codes
const CONSIDERATION_FORMS = [
  { value: "30", label: "30 — Cash" },
  { value: "31", label: "31 — Debt (mortgage etc.)" },
  { value: "32", label: "32 — Building works" },
  { value: "33", label: "33 — Services / employment" },
  { value: "34", label: "34 — Shares / securities" },
  { value: "35", label: "35 — Other goods" },
  { value: "36", label: "36 — Annuities" },
  { value: "37", label: "37 — Other consideration" },
];

const RELIEF_CODES = [
  { value: "none", label: "No relief claimed" },
  { value: "28", label: "28 — First-time buyer relief" },
  { value: "32", label: "32 — Multiple Dwellings Relief (transitional only — abolished 01-Jun-2024)" },
  { value: "31", label: "31 — Charities relief" },
  { value: "12", label: "12 — Group relief" },
  { value: "23", label: "23 — Right-to-buy" },
  { value: "33", label: "33 — Sub-sale relief" },
  { value: "34", label: "34 — Acquisition relief" },
  { value: "35", label: "35 — Reconstruction relief" },
  { value: "36", label: "36 — Property authorised investment funds (PAIFs)" },
  { value: "37", label: "37 — Crofting community right to buy" },
  { value: "38", label: "38 — Seeding relief" },
  { value: "39", label: "39 — Compliance with planning obligations" },
  { value: "41", label: "41 — Freeport / Investment Zone tax site" },
];

const ID_TYPES = [
  { value: "nino", label: "National Insurance number (1.49)" },
  { value: "vat", label: "VAT registration number (1.50)" },
  { value: "utr", label: "UTR — Company / Partnership (1.51)" },
  { value: "passport_dob", label: "DOB + Passport / driving licence" },
  { value: "company_no", label: "Companies House number" },
];

const PAYMENT_METHODS = [
  { value: "electronic", label: "Electronic / BACS" },
  { value: "chaps", label: "CHAPS" },
  { value: "cheque", label: "Cheque" },
  { value: "card", label: "Card payment" },
  { value: "not_paid", label: "Not yet paid" },
];

const SDLT5_DESTINATIONS = [
  { value: "property", label: "Property" },
  { value: "purchaser", label: "Purchaser" },
  { value: "agent", label: "Agent" },
];

const DECLARATION_OPTIONS = [
  { value: "1", label: "Declaration 1 — filing for yourself / as in-house representative" },
  { value: "2", label: "Declaration 2 — on behalf of a purchaser who has approved the effective date" },
  { value: "3", label: "Declaration 3 — on behalf of a purchaser who authorised you to enter the effective date" },
];

const DECLARATION_TEXT = {
  "1": "The information contained in this return is correct and complete to the best of my knowledge and belief.",
  "2": "I confirm that the purchaser(s) has / have declared that the information contained in this return is correct and complete to the best of the knowledge and belief of the purchaser(s) and that the purchaser(s) has / have authorised me to submit the return electronically on behalf of the purchaser(s).",
  "3": "I confirm that the purchaser(s) has / have declared that the information contained in this return, with the exception of the effective date, is correct and complete to the best of the knowledge and belief of the purchaser(s). The purchaser(s) has / have authorised me to enter the effective date and to submit this return electronically on behalf of the purchaser(s). The effective date is correct and complete to the best of my knowledge and belief.",
};

const SIGN_CAPACITY = [
  { value: "purchaser", label: "Purchaser" },
  { value: "agent", label: "Authorised agent (conveyancer)" },
  { value: "director", label: "Director" },
  { value: "partner", label: "Partner" },
  { value: "trustee", label: "Trustee" },
];

// ---- Defaults / validation -----------------------------------------------

const PURCHASER_DESCRIPTIONS = [
  { value: "01", label: "01 Unincorporated builder" },
  { value: "02", label: "02 Unincorporated sole trader other than a builder" },
  { value: "03", label: "03 Individual other than sole trader" },
  { value: "04", label: "04 Partnership" },
  { value: "05", label: "05 Local authority" },
  { value: "06", label: "06 Central government" },
  { value: "07", label: "07 Public corporation" },
  { value: "08", label: "08 Property company" },
  { value: "09", label: "09 Bank" },
  { value: "10", label: "10 Building society" },
  { value: "11", label: "11 Insurance / assurance company" },
  { value: "12", label: "12 Superannuation or pension fund" },
  { value: "13", label: "13 Other financial institution" },
  { value: "14", label: "14 Other company" },
  { value: "15", label: "15 Other, including charity" },
];

function emptyParty() {
  return { type: "individual", title: "", first_name: "", middle_name: "", surname: "", company_name: "", id_type: "nino", id_value: "", utr: "", vat_number: "", place_of_registration: "", dob: "", address_1: "", address_2: "", town: "", postcode: "", country: "United Kingdom", agent_ref: "", lead_buyer: false, has_nino: "yes", acting_as_trustee: "no", connected_to_vendor: "no", represented_by_agent: "yes", purchaser_description: [] };
}

function todayIso() { return new Date().toISOString().slice(0, 10); }

// A stored party counts as "populated" only if it has a real name or address.
function partyHasContent(p) {
  return !!(p && (p.surname || p.company_name || p.first_name || p.address_1 || p.postcode));
}

// Split a combined "name" string into title / first name(s) / surname.
const NAME_TITLES = ["mr", "mrs", "miss", "ms", "dr", "prof", "sir", "lady", "mx", "rev"];
function splitName(full) {
  const parts = (full || "").trim().split(/\s+/).filter(Boolean);
  let title = "";
  if (parts.length && NAME_TITLES.includes(parts[0].toLowerCase().replace(/\.$/, ""))) title = parts.shift();
  if (!parts.length) return { title, first: "", surname: "" };
  if (parts.length === 1) return { title, first: "", surname: parts[0] };
  return { title, first: parts.slice(0, -1).join(" "), surname: parts[parts.length - 1] };
}

// Split a combined address string (may end with a UK postcode) into parts.
function splitAddress(addr) {
  const raw = (addr || "").trim();
  if (!raw) return { line1: "", line2: "", town: "", postcode: "" };
  const pcRe = /([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})\s*$/i;
  let postcode = "", rest = raw;
  const m = raw.match(pcRe);
  if (m) {
    postcode = m[1].toUpperCase().replace(/\s+/g, " ").replace(/(\d[A-Z]{2})$/, " $1").replace(/\s+/g, " ").trim();
    rest = raw.slice(0, m.index).replace(/[,\s]+$/, "");
  }
  const segs = rest.split(",").map((s) => s.trim()).filter(Boolean);
  const line1 = segs[0] || "";
  const town = segs.length > 1 ? segs[segs.length - 1] : "";
  const line2 = segs.length > 2 ? segs.slice(1, -1).join(", ") : "";
  return { line1, line2, town, postcode };
}


export function defaultsSdlt(caseData, item) {
  const m = item?.metadata || {};
  const mapParty = (c) => {
    const nm = (c.first_name || c.surname || c.last_name)
      ? { title: c.title || "", first: c.first_name || "", surname: c.surname || c.last_name || "" }
      : splitName(c.name || "");
    const structured = c.postcode || c.town || c.address_line2 || c.address_line_2;
    const ad = structured
      ? { line1: c.address_line1 || c.address_line_1 || "", line2: c.address_line2 || c.address_line_2 || "", town: c.town || c.city || "", postcode: c.postcode || "" }
      : splitAddress(c.address_line1 || c.address_line_1 || c.address || "");
    const isCompany = c.is_company || /\b(ltd|limited|plc|llp|co\.?)\b/i.test(c.name || c.company_name || "");
    return {
      ...emptyParty(),
      type: isCompany ? "company" : "individual",
      title: nm.title,
      first_name: nm.first,
      middle_name: c.middle_name || "",
      surname: nm.surname,
      company_name: isCompany ? (c.company_name || c.name || "") : "",
      id_type: c.nino ? "nino" : (isCompany ? "company_no" : "nino"),
      id_value: c.nino || c.company_number || c.company_no || "",
      has_nino: c.nino ? "yes" : "no",
      utr: c.utr || "",
      place_of_registration: c.place_of_registration || c.country_of_registration || "",
      dob: c.date_of_birth || c.dob || "",
      address_1: ad.line1,
      address_2: ad.line2,
      town: ad.town,
      postcode: ad.postcode,
      country: c.country || "United Kingdom",
      lead_buyer: false,
    };
  };
  const allClients = caseData?.clients || [];
  const isSellerRole = (r) => /seller|vendor/i.test(r || "") && !/solicitor|conveyanc|agent|estate/i.test(r || "");
  const buyers = allClients.filter((c) => !isSellerRole(c.case_role)).map(mapParty);
  // Vendors come from client rows tagged as seller AND from "Additional parties" with a Seller/Vendor role.
  const sellerClients = allClients.filter((c) => isSellerRole(c.case_role)).map(mapParty);
  const sellerParties = (caseData?.parties || []).filter((p) => isSellerRole(p.role)).map(mapParty);
  const sellers = [...sellerClients, ...sellerParties];
  if (buyers.length && !buyers.some((b) => b.lead_buyer)) buyers[0].lead_buyer = true;
  // Derive HMRC values from the stored case where possible.
  const caseValue = caseData?.property_value || caseData?.purchase_price || "";
  const caseTenure = (caseData?.tenure || "").toLowerCase();
  const derivedTenure = ["freehold", "leasehold", "commonhold"].includes(caseTenure) ? caseTenure : "freehold";
  const derivedInterest = derivedTenure === "leasehold" ? "LP" : derivedTenure === "commonhold" ? "OT" : "FP";
  const derivedTxnType = derivedTenure === "leasehold" ? "leasehold_assigned" : "freehold";
  // HMRC SDLT1 Q2 (description of transaction) defaults to F — Conveyance /
  // transfer — for the overwhelming majority of returns, INCLUDING the
  // transfer of an existing leasehold interest (reported on the consideration
  // with NO lease boxes). Only the GRANT of a brand-new lease is code L, which
  // the solicitor selects explicitly. Box 2 is independent of the interest's
  // tenure in Q3.
  const derivedTxnDesc = "F";
  const caseTypeStr = ((caseData?.case_type || "") + " " + (caseData?.property_type || "")).toLowerCase();
  const derivedPropType = /commercial|non.?res|office|retail|industrial/.test(caseTypeStr) ? "03" : /mixed/.test(caseTypeStr) ? "02" : "01";
  const propAddr = (caseData?.property_postcode || caseData?.property_town)
    ? { line1: caseData?.property_address || "", line2: "", town: caseData?.property_town || "", postcode: caseData?.property_postcode || "" }
    : splitAddress(caseData?.property_address || "");
  return {
    // --- Purchasers & vendors ---
    vendors: (m.vendors || []).some(partyHasContent) ? m.vendors : (sellers.length ? sellers : [emptyParty()]),
    purchasers: (m.purchasers || []).some(partyHasContent) ? m.purchasers : (buyers.length ? buyers : [{ ...emptyParty(), lead_buyer: true }]),
    multiple_purchasers_indicator: m.multiple_purchasers_indicator || (buyers.length > 1 ? "yes" : "no"),
    // --- About the land ---
    property_type: m.property_type || derivedPropType,
    interest_transferred: (["FP", "FG", "FT", "LP", "LG", "LT", "OT"].includes(m.interest_transferred) ? m.interest_transferred : derivedInterest),
    property_subtypes: m.property_subtypes?.length ? m.property_subtypes : ["standard_residential"],
    predominantly_residential: m.predominantly_residential || "yes",
    property_address_1: m.property_address_1 || propAddr.line1,
    property_address_2: m.property_address_2 || propAddr.line2,
    property_town: m.property_town || propAddr.town,
    property_postcode: m.property_postcode || propAddr.postcode,
    local_authority_code: m.local_authority_code || caseData?.local_authority_code || caseData?.la_code || "",
    title_number: m.title_number || caseData?.title_number || "",
    uprn: m.uprn || "",
    agricultural_area_unit: m.agricultural_area_unit || "na",
    agricultural_area: m.agricultural_area || "",
    sending_plan_by_post: m.sending_plan_by_post || "no",
    number_of_properties: m.number_of_properties || "1",
    certificate_per_property: m.certificate_per_property || "no",
    apportionment_land: m.apportionment_land || "",
    apportionment_buildings: m.apportionment_buildings || "",
    // --- About UK residency ---
    any_purchaser_non_uk_resident: m.any_purchaser_non_uk_resident || (caseData?.non_uk_resident ? "yes" : "no"),
    crown_employment_relief: m.crown_employment_relief || "no",
    // --- About the transaction ---
    // Auto-heal legacy data: older builds auto-defaulted every leasehold case
    // to box 2 = "A" (assignment), which makes HMRC demand lease boxes 16-20.
    // A deliberate assignment/new-lease always carries lease particulars, so a
    // stored "A"/"S" with NO lease term or start date is the legacy auto-value
    // — normalise it back to "F" (conveyance/transfer).
    transaction_description: ((m.transaction_description === "A" || m.transaction_description === "S") && !m.lease_term_years && !m.lease_start_date)
      ? "F"
      : (m.transaction_description || derivedTxnDesc),
    transaction_type: m.transaction_type || derivedTxnType,
    tenure: m.tenure || derivedTenure,
    effective_date: m.effective_date || caseData?.target_completion_date || todayIso(),
    contract_date: m.contract_date || caseData?.exchange_date || "",
    linked_transactions: m.linked_transactions || "no",
    part_of_business_sale: m.part_of_business_sale || "no",
    cap1_nsbc_ruling: m.cap1_nsbc_ruling || "no",
    restrictions_affecting_value: m.restrictions_affecting_value || "no",
    land_exchanged: m.land_exchanged || "no",
    pursuant_to_option: m.pursuant_to_option || "no",
    // --- Consideration ---
    total_consideration: m.total_consideration || caseValue || "",
    cash_consideration: m.cash_consideration || caseValue || "",
    mortgage_amount: m.mortgage_amount || caseData?.mortgage_amount || "",
    other_consideration: m.other_consideration || "",
    market_value: m.market_value || "",
    consideration_form: m.consideration_form || "30",
    vat_included: m.vat_included || "no",
    chargeable_consideration: m.chargeable_consideration || caseValue || "",
    contingent_or_uncertain: m.contingent_or_uncertain || "no",
    contingent_amount: m.contingent_amount || "",
    // --- Reliefs ---
    relief_code: m.relief_code || (caseData?.first_time_buyer ? "28" : "none"),
    relief_amount: m.relief_amount || "",
    relief_details: m.relief_details || "",
    first_time_buyer: m.first_time_buyer || (caseData?.first_time_buyer ? "yes" : "no"),
    additional_property: m.additional_property || (caseData?.additional_property ? "yes" : "no"),
    non_uk_resident_surcharge: m.non_uk_resident_surcharge || (caseData?.non_uk_resident ? "yes" : "no"),
    // --- Lease ---
    lease_term_years: m.lease_term_years || "",
    lease_start_date: m.lease_start_date || "",
    lease_premium: m.lease_premium || "",
    lease_rent_y1: m.lease_rent_y1 || "",
    lease_rent_y2: m.lease_rent_y2 || "",
    lease_rent_y3: m.lease_rent_y3 || "",
    lease_rent_y4: m.lease_rent_y4 || "",
    lease_rent_y5: m.lease_rent_y5 || "",
    // --- Filing agent (acting for the PURCHASER — SDLT1 Q61-66) ---
    has_agent: m.has_agent || "yes",
    agent_handle_correspondence: m.agent_handle_correspondence || "yes",
    sdlt5_certificate_to: m.sdlt5_certificate_to || "agent",
    agent_firm_name: m.agent_firm_name || "UKC Legal Solicitors",
    agent_reference: m.agent_reference || caseData?.reference || "",
    agent_address_1: m.agent_address_1 || "38 Prince of Wales Road",
    agent_address_2: m.agent_address_2 || "",
    agent_town: m.agent_town || "Norwich",
    agent_postcode: m.agent_postcode || "NR1 1LG",
    agent_dx: m.agent_dx || "",
    agent_phone: m.agent_phone || "01603 339174",
    agent_email: m.agent_email || "",
    // --- Agent acting for the VENDOR (seller's conveyancer — SDLT1 Q39-44) ---
    vendor_agent_name: m.vendor_agent_name || "",
    vendor_agent_address_1: m.vendor_agent_address_1 || "",
    vendor_agent_address_2: m.vendor_agent_address_2 || "",
    vendor_agent_town: m.vendor_agent_town || "",
    vendor_agent_postcode: m.vendor_agent_postcode || "",
    vendor_agent_dx: m.vendor_agent_dx || "",
    vendor_agent_email: m.vendor_agent_email || "",
    vendor_agent_reference: m.vendor_agent_reference || "",
    vendor_agent_phone: m.vendor_agent_phone || "",
    // --- Tax & payment ---
    tax_band_breakdown: m.tax_band_breakdown || "",
    total_tax_due: m.total_tax_due || "",
    amount_paid: m.amount_paid || "",
    amount_paid_includes_penalties: m.amount_paid_includes_penalties || "no",
    tax_paid: m.tax_paid || "no",
    payment_method: m.payment_method || "electronic",
    payment_reference: m.payment_reference || "",
    // --- Declaration ---
    signing_capacity: m.signing_capacity || "agent",
    declaration_type: m.declaration_type || "3",
    signing_name: m.signing_name || caseData?.solicitor_name || "",
    signing_date: m.signing_date || todayIso(),
  };
}

export function validateSdlt(v) {
  const errs = {};
  if (!(v.purchasers || []).length) errs.purchasers = "At least 1 purchaser";
  (v.purchasers || []).forEach((p, i) => {
    if (p.type === "individual" && !p.first_name) errs[`purchaser_${i}_first_name`] = "Required";
    if (p.type === "individual" && !p.surname) errs[`purchaser_${i}_surname`] = "Required";
    if (p.type === "company" && !p.company_name) errs[`purchaser_${i}_company`] = "Required";
    if (!p.postcode) errs[`purchaser_${i}_postcode`] = "Required";
    if (!p.id_type) errs[`purchaser_${i}_id_type`] = "Required";
  });
  if (!(v.vendors || []).length) errs.vendors = "At least 1 vendor";
  (v.vendors || []).forEach((p, i) => {
    if (p.type === "individual" && !p.first_name) errs[`vendor_${i}_first_name`] = "Required";
    if (p.type === "individual" && !p.surname) errs[`vendor_${i}_surname`] = "Required";
    if (p.type === "company" && !p.company_name) errs[`vendor_${i}_company`] = "Required";
    if (!p.postcode) errs[`vendor_${i}_postcode`] = "Required";
  });
  // Vendor agent: if any detail is entered, the agent name is mandatory (HMRC Q39).
  if ((v.vendor_agent_address_1 || v.vendor_agent_postcode || v.vendor_agent_reference || v.vendor_agent_phone || v.vendor_agent_email || v.vendor_agent_dx) && !v.vendor_agent_name) {
    errs.vendor_agent_name = "Enter the vendor's agent name";
  }
  if (!v.property_type) errs.property_type = "Required (Q1)";
  if (!v.interest_transferred) errs.interest_transferred = "Required (Q3)";
  if (!v.property_address_1) errs.property_address_1 = "Required (Q28)";
  if (!v.property_town) errs.property_town = "Required (Q28)";
  if (!v.local_authority_code) errs.local_authority_code = "Required (Q29)";
  if (!v.transaction_description) errs.transaction_description = "Required (Q2)";
  // Q2 (description of transaction) is INDEPENDENT of the interest's tenure in
  // Q3: a conveyance/transfer (F) legitimately conveys a freehold OR an
  // existing leasehold interest, with no lease boxes. The lease-detail boxes
  // (16-20) are driven solely by code L (grant of a new lease), validated
  // below. Only guard the genuinely contradictory combination.
  if (v.transaction_description === "A" && v.transaction_type === "leasehold_new") {
    errs.transaction_description = "Use code L for the grant of a new lease.";
  }
  if (!v.effective_date) errs.effective_date = "Required (Q4)";
  if (!v.total_tax_due && Number(v.total_tax_due) !== 0) errs.total_tax_due = "Required (Q14)";
  if (v.amount_paid === "" || v.amount_paid === undefined || v.amount_paid === null) errs.amount_paid = "Required (Q15)";
  if (!v.chargeable_consideration || Number(v.chargeable_consideration) <= 0) errs.chargeable_consideration = "Must be > 0";
  if (!v.total_consideration || Number(v.total_consideration) <= 0) errs.total_consideration = "Must be > 0 (1.65)";
  if (v.transaction_description === "L") {
    if (!v.lease_term_years) errs.lease_term_years = "Required for a new lease";
    if (!v.lease_start_date) errs.lease_start_date = "Required for a new lease";
  }
  if (!v.signing_name) errs.signing_name = "Declaration must be signed";
  return errs;
}

// ---- Sub-component: Party Editor ----------------------------------------

function PartyEditor({ party, idx, prefix, onChange, onRemove, errors, allowLead }) {
  const set = (k, val) => onChange({ ...party, [k]: val });
  const isCompany = party.type === "company";
  const isPurchaser = prefix === "purchaser";
  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-3 bg-slate-50/40" data-testid={`${prefix}-${idx}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
          {isPurchaser ? "Purchaser" : "Vendor"} #{idx + 1}
          {allowLead && party.lead_buyer && (
            <span className="ml-2 text-[10px] bg-[#1462a0] text-white px-1.5 py-0.5 rounded">Lead buyer</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {allowLead && (
            <label className="text-[11px] text-slate-600 inline-flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={!!party.lead_buyer} onChange={(e) => set("lead_buyer", e.target.checked)} data-testid={`${prefix}-lead-${idx}`} />
              Lead buyer
            </label>
          )}
          {onRemove && (
            <Button size="sm" variant="ghost" onClick={onRemove} className="text-rose-600 h-7 px-2" data-testid={`${prefix}-remove-${idx}`}>
              <Trash weight="bold" className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>
      <PanelGrid>
        <SelectField name={`${prefix}_${idx}_type`} label="Is this party an individual?" required value={party.type} onChange={(x) => set("type", x)} options={[{ value: "individual", label: "Individual" }, { value: "company", label: "Company / Trust / Other" }]} />
        {isCompany ? (
          <TextField name={`${prefix}_${idx}_company`} label={`Surname or company name (${isPurchaser ? 54 : 36})`} required value={party.company_name} onChange={(x) => set("company_name", x)} error={errors[`${prefix}_${idx}_company`]} span={2} />
        ) : (
          <>
            <TextField name={`${prefix}_${idx}_title`} label={`Title (${isPurchaser ? 53 : 35})`} value={party.title} onChange={(x) => set("title", x)} placeholder="Mr / Mrs / Dr" />
            <TextField name={`${prefix}_${idx}_first_name`} label={`First name (${isPurchaser ? 55 : 37})`} required value={party.first_name} onChange={(x) => set("first_name", x)} error={errors[`${prefix}_${idx}_first_name`]} />
            <TextField name={`${prefix}_${idx}_middle_name`} label="Middle name" value={party.middle_name} onChange={(x) => set("middle_name", x)} />
            <TextField name={`${prefix}_${idx}_surname`} label={`Surname or company name (${isPurchaser ? 54 : 36})`} required value={party.surname} onChange={(x) => set("surname", x)} error={errors[`${prefix}_${idx}_surname`]} />
          </>
        )}
        {isPurchaser && (
          <>
            {!isCompany && (
              <YesNoField name={`${prefix}_${idx}_has_nino`} label="Does this purchaser have a National Insurance Number? (49)" value={party.has_nino} onChange={(x) => set("has_nino", x)} />
            )}
            <YesNoField name={`${prefix}_${idx}_trustee`} label="Is the purchaser acting as a trustee? (57)" value={party.acting_as_trustee} onChange={(x) => set("acting_as_trustee", x)} />
            <YesNoField name={`${prefix}_${idx}_connected`} label="Are the purchaser and vendor connected? (59)" value={party.connected_to_vendor} onChange={(x) => set("connected_to_vendor", x)} />
          </>
        )}
        {isPurchaser && (
          <>
            <SelectField name={`${prefix}_${idx}_id_type`} label="Type of reference (49 / 50 / 51)" required value={party.id_type} onChange={(x) => set("id_type", x)} options={ID_TYPES} error={errors[`${prefix}_${idx}_id_type`]} />
            <TextField name={`${prefix}_${idx}_id_value`} label="Identifier value" hint={party.id_type === "nino" ? "NINO e.g. QQ123456C (49)" : party.id_type === "company_no" ? "Companies House number, e.g. 12345678 (box 51)" : party.id_type === "vat" ? "9-digit VAT number (box 50)" : party.id_type === "utr" ? "10-digit Corporation Tax UTR (box 51)" : ""} value={party.id_value} onChange={(x) => set("id_value", x)} />
            <TextField name={`${prefix}_${idx}_utr`} label="UTR — Company / Partnership (51)" value={party.utr} onChange={(x) => set("utr", x)} />
            <TextField name={`${prefix}_${idx}_vat`} label="VAT number (50)" value={party.vat_number} onChange={(x) => set("vat_number", x)} />
            {isCompany && (
              <TextField name={`${prefix}_${idx}_place_of_registration`} label="Country of registration (box 51 part 3)" hint="Required by HMRC when a company reference is given, e.g. United Kingdom, Jersey, BVI" value={party.place_of_registration} onChange={(x) => set("place_of_registration", x)} />
            )}
          </>
        )}
        {isPurchaser && !isCompany && (
          <DateField name={`${prefix}_${idx}_dob`} label="Date of birth (49)" value={party.dob} onChange={(x) => set("dob", x)} />
        )}
        <TextField name={`${prefix}_${idx}_address_1`} label={`Address line 1 (${isPurchaser ? 56 : 38})`} value={party.address_1} onChange={(x) => set("address_1", x)} span={2} />
        <TextField name={`${prefix}_${idx}_address_2`} label="Address line 2" value={party.address_2} onChange={(x) => set("address_2", x)} />
        <TextField name={`${prefix}_${idx}_town`} label="Town" value={party.town} onChange={(x) => set("town", x)} />
        <TextField name={`${prefix}_${idx}_postcode`} label={`Postcode (${isPurchaser ? 56 : 38})`} required value={party.postcode} onChange={(x) => set("postcode", x)} error={errors[`${prefix}_${idx}_postcode`]} />
        <TextField name={`${prefix}_${idx}_country`} label="Country" value={party.country} onChange={(x) => set("country", x)} />
        {isPurchaser && (
          <YesNoField name={`${prefix}_${idx}_rep_agent`} label="Do the purchasers authorise an agent to handle correspondence? (61)" value={party.represented_by_agent} onChange={(x) => set("represented_by_agent", x)} />
        )}
        {isPurchaser && (
          <MultiCheck name={`${prefix}_${idx}_purchaser_description`} label="Purchaser description" hint="Tick all that apply (HMRC codes 01–15)" value={party.purchaser_description || []} onChange={(x) => set("purchaser_description", x)} options={PURCHASER_DESCRIPTIONS} />
        )}
      </PanelGrid>
    </div>
  );
}

// ---- Multi-checkbox helper ----------------------------------------------

function MultiCheck({ name, label, hint, value = [], onChange, options }) {
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div className="sm:col-span-3 space-y-2" data-testid={`field-${name}`}>
      <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-600">{label}</div>
      {hint && <p className="text-[11px] text-slate-500 -mt-1">{hint}</p>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
        {options.map((o) => (
          <label key={o.value} className="text-xs inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={value.includes(o.value)} onChange={() => toggle(o.value)} data-testid={`field-${name}-${o.value}`} />
            {o.label}
          </label>
        ))}
      </div>
    </div>
  );
}

// ---- Panels (in HMRC Online order) --------------------------------------

export function SdltPanelPurchasers({ v, set, e }) {
  const list = v.purchasers || [];
  const update = (i, party) => {
    if (party.lead_buyer) set("purchasers", list.map((p, idx) => ({ ...p, lead_buyer: idx === i })));
    else set("purchasers", list.map((p, idx) => idx === i ? party : p));
  };
  const remove = (i) => set("purchasers", list.filter((_, idx) => idx !== i));
  const add = () => list.length < 9 && set("purchasers", [...list, { ...emptyParty() }]);
  return (
    <div className="space-y-3 py-3" data-testid="sdlt-purchasers-panel">
      <p className="text-xs text-slate-500">Purchasers: <strong>{list.length}</strong> · tick the lead purchaser</p>
      {list.map((p, i) => (
        <PartyEditor key={i} party={p} idx={i} prefix="purchaser" onChange={(np) => update(i, np)} onRemove={list.length > 1 ? () => remove(i) : null} errors={e} allowLead />
      ))}
      {list.length < 9 && (
        <Button size="sm" variant="outline" onClick={add} className="border-dashed" data-testid="sdlt-add-purchaser">
          <Plus weight="bold" className="w-4 h-4 mr-1" /> Add another purchaser (max 9)
        </Button>
      )}
    </div>
  );
}

export function SdltPanelVendors({ v, set, e }) {
  const list = v.vendors || [];
  const update = (i, party) => set("vendors", list.map((p, idx) => idx === i ? party : p));
  const remove = (i) => set("vendors", list.filter((_, idx) => idx !== i));
  const add = () => list.length < 9 && set("vendors", [...list, emptyParty()]);
  return (
    <div className="space-y-3 py-3" data-testid="sdlt-vendors-panel">
      <p className="text-xs text-slate-500">Vendors: <strong>{list.length}</strong></p>
      {list.map((p, i) => (
        <PartyEditor key={i} party={p} idx={i} prefix="vendor" onChange={(np) => update(i, np)} onRemove={list.length > 1 ? () => remove(i) : null} errors={e} />
      ))}
      {list.length < 9 && (
        <Button size="sm" variant="outline" onClick={add} className="border-dashed" data-testid="sdlt-add-vendor">
          <Plus weight="bold" className="w-4 h-4 mr-1" /> Add another vendor (max 9)
        </Button>
      )}

      <div className="border border-slate-200 rounded-lg p-3 space-y-3 bg-slate-50/40 mt-2" data-testid="sdlt-vendor-agent">
        <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
          Agent acting for the vendor(s) — SDLT1 Q39–44
        </div>
        <p className="text-[11px] text-slate-500 -mt-1">The seller's conveyancer. Leave blank if the vendor is unrepresented.</p>
        <PanelGrid>
          <TextField name="vendor_agent_name" label="Agent / firm name (Q39)" hint="Max 28 characters" value={v.vendor_agent_name} onChange={(x) => set("vendor_agent_name", x)} span={2} error={e.vendor_agent_name} />
          <TextField name="vendor_agent_reference" label="Their reference (Q43)" hint="Max 14 characters" value={v.vendor_agent_reference} onChange={(x) => set("vendor_agent_reference", x)} />
          <TextField name="vendor_agent_address_1" label="Address line 1 (Q40)" value={v.vendor_agent_address_1} onChange={(x) => set("vendor_agent_address_1", x)} span={2} />
          <TextField name="vendor_agent_address_2" label="Address line 2" value={v.vendor_agent_address_2} onChange={(x) => set("vendor_agent_address_2", x)} />
          <TextField name="vendor_agent_town" label="Town" value={v.vendor_agent_town} onChange={(x) => set("vendor_agent_town", x)} />
          <TextField name="vendor_agent_postcode" label="Postcode (Q40)" value={v.vendor_agent_postcode} onChange={(x) => set("vendor_agent_postcode", x)} />
          <TextField name="vendor_agent_dx" label="DX number & exchange (Q41)" value={v.vendor_agent_dx} onChange={(x) => set("vendor_agent_dx", x)} />
          <TextField name="vendor_agent_email" label="Email (Q42)" type="email" hint="Max 36 characters" value={v.vendor_agent_email} onChange={(x) => set("vendor_agent_email", x)} />
          <TextField name="vendor_agent_phone" label="Telephone (Q44)" hint="Max 14 characters" value={v.vendor_agent_phone} onChange={(x) => set("vendor_agent_phone", x)} />
        </PanelGrid>
      </div>
    </div>
  );
}

export function SdltPanelLand({ v, set, e }) {
  return (
    <PanelGrid>
      <SelectField name="property_type" label="Type of property (Q1)" required value={v.property_type} onChange={(x) => set("property_type", x)} options={PROPERTY_TYPES} error={e.property_type} />
      <SelectField name="interest_transferred" label="Interest transferred or created (Q3)" required value={v.interest_transferred} onChange={(x) => set("interest_transferred", x)} options={INTEREST_TRANSFERRED} error={e.interest_transferred} />
      {v.property_type === "02" && (
        <YesNoField name="predominantly_residential" label="Predominantly residential? (Q1)" value={v.predominantly_residential} onChange={(x) => set("predominantly_residential", x)} />
      )}
      <TextField name="property_address_1" label="Address line 1 (Q28)" required value={v.property_address_1} onChange={(x) => set("property_address_1", x)} error={e.property_address_1} span={2} />
      <TextField name="property_address_2" label="Address line 2 (Q28)" value={v.property_address_2} onChange={(x) => set("property_address_2", x)} span={2} />
      <TextField name="property_town" label="Town / City (Q28)" required value={v.property_town} onChange={(x) => set("property_town", x)} error={e.property_town} />
      <TextField name="property_postcode" label="Postcode (Q28)" value={v.property_postcode} onChange={(x) => set("property_postcode", x)} error={e.property_postcode} />
      <ComboboxField name="local_authority_code" label="Local authority (Q29)" required hint="HMRC local-authority code — mandatory. Type to search by name." value={v.local_authority_code} onChange={(x) => set("local_authority_code", x)} options={LA_OPTIONS} error={e.local_authority_code} />
      <TextField name="title_number" label="Title number, if any (Q30)" hint="e.g. NK123456" value={v.title_number} onChange={(x) => set("title_number", x)} />
      <TextField name="uprn" label="NLPG UPRN (Q31)" hint="Unique Property Reference Number" value={v.uprn} onChange={(x) => set("uprn", x)} />
      <SelectField name="agricultural_area_unit" label="Agricultural/development land — unit (1.32)" value={v.agricultural_area_unit} onChange={(x) => set("agricultural_area_unit", x)} options={AREA_UNITS} />
      {v.agricultural_area_unit !== "na" && (
        <NumberField name="agricultural_area" label="Area (1.32)" hint="Accurate to 3 decimal places" step="0.001" value={v.agricultural_area} onChange={(x) => set("agricultural_area", x)} />
      )}
      <YesNoField name="sending_plan_by_post" label="Plan submitted? (Q33)" required value={v.sending_plan_by_post} onChange={(x) => set("sending_plan_by_post", x)} />
      <NumberField name="number_of_properties" label="No. of properties (1.26)" step="1" value={v.number_of_properties} onChange={(x) => set("number_of_properties", x)} />
      {Number(v.number_of_properties) > 1 && (
        <YesNoField name="certificate_per_property" label="SDLT5 certificate per property? (1.27)" value={v.certificate_per_property} onChange={(x) => set("certificate_per_property", x)} />
      )}
      <MultiCheck name="property_subtypes" label="Property characteristics" hint="Tick all that apply" value={v.property_subtypes || []} onChange={(x) => set("property_subtypes", x)} options={PROPERTY_SUBTYPES} />
    </PanelGrid>
  );
}

export function SdltPanelResidency({ v, set }) {
  return (
    <div className="space-y-3 py-1">
      <p className="text-xs text-slate-500 leading-relaxed">Answer only if the effective date is on or after 1 April 2021 and the property is residential (or additional residential liable to higher rate). Do not complete for mixed-use or non-residential.</p>
      <PanelGrid>
        <YesNoField name="any_purchaser_non_uk_resident" label="Are any of the purchasers non-UK residents?" value={v.any_purchaser_non_uk_resident} onChange={(x) => set("any_purchaser_non_uk_resident", x)} />
        <YesNoField name="crown_employment_relief" label="Are you claiming Crown Employment Relief?" value={v.crown_employment_relief} onChange={(x) => set("crown_employment_relief", x)} />
      </PanelGrid>
    </div>
  );
}

export function SdltPanelTransaction({ v, set, e }) {
  return (
    <PanelGrid>
      <SelectField name="transaction_description" label="Description of transaction (1.2)" required value={v.transaction_description} onChange={(x) => set("transaction_description", x)} options={TRANSACTION_DESCRIPTIONS} error={e.transaction_description} />
      <DateField name="effective_date" label="Effective date of transaction (1.4)" required hint="Usually completion — affects penalties & tax due" value={v.effective_date} onChange={(x) => set("effective_date", x)} error={e.effective_date} />
      <DateField name="contract_date" label="Date of contract or conclusion of missives (1.6)" value={v.contract_date} onChange={(x) => set("contract_date", x)} />
      <SelectField name="tenure" label="Tenure" value={v.tenure} onChange={(x) => set("tenure", x)} options={TENURE_TYPES} />
      <SelectField name="transaction_type" label="Transaction type (internal)" value={v.transaction_type} onChange={(x) => set("transaction_type", x)} options={TRANSACTION_TYPES} />
      <YesNoField name="linked_transactions" label="Is this transaction linked to any other?" value={v.linked_transactions} onChange={(x) => set("linked_transactions", x)} />
      <YesNoField name="multiple_purchasers_indicator" label="More than one purchaser?" value={v.multiple_purchasers_indicator} onChange={(x) => set("multiple_purchasers_indicator", x)} />
      <YesNoField name="part_of_business_sale" label="Is this transaction part of the sale of a business?" value={v.part_of_business_sale} onChange={(x) => set("part_of_business_sale", x)} />
      <YesNoField name="cap1_nsbc_ruling" label="Applied for a ruling under CAP1 or NSBC?" value={v.cap1_nsbc_ruling} onChange={(x) => set("cap1_nsbc_ruling", x)} />
      <YesNoField name="restrictions_affecting_value" label="Any restrictions, covenants or conditions affecting the value?" value={v.restrictions_affecting_value} onChange={(x) => set("restrictions_affecting_value", x)} />
      <YesNoField name="land_exchanged" label="Is any land exchanged or part-exchanged?" value={v.land_exchanged} onChange={(x) => set("land_exchanged", x)} />
      <YesNoField name="pursuant_to_option" label="Is the transaction pursuant to a previous option agreement?" value={v.pursuant_to_option} onChange={(x) => set("pursuant_to_option", x)} />
    </PanelGrid>
  );
}

export function SdltPanelConsideration({ v, set, e }) {
  return (
    <PanelGrid>
      <NumberField name="total_consideration" label="Total consideration incl. VAT (£) (1.65)" required value={v.total_consideration} onChange={(x) => set("total_consideration", x)} error={e.total_consideration} />
      <NumberField name="cash_consideration" label="Cash portion (£)" value={v.cash_consideration} onChange={(x) => set("cash_consideration", x)} />
      <NumberField name="mortgage_amount" label="Mortgage / loan included (£) (1.66)" value={v.mortgage_amount} onChange={(x) => set("mortgage_amount", x)} />
      <NumberField name="other_consideration" label="Other consideration (£) (1.67)" hint="Non-monetary" value={v.other_consideration} onChange={(x) => set("other_consideration", x)} />
      <NumberField name="market_value" label="Market value (£) (1.68)" hint="If transfer for nil/under-value" value={v.market_value} onChange={(x) => set("market_value", x)} />
      <SelectField name="consideration_form" label="Form of consideration (1.70)" value={v.consideration_form} onChange={(x) => set("consideration_form", x)} options={CONSIDERATION_FORMS} />
      <YesNoField name="vat_included" label="VAT included in price?" value={v.vat_included} onChange={(x) => set("vat_included", x)} />
      <YesNoField name="contingent_or_uncertain" label="Contingent / uncertain consideration? (1.71)" hint="e.g. overage / clawback" value={v.contingent_or_uncertain} onChange={(x) => set("contingent_or_uncertain", x)} />
      {v.contingent_or_uncertain === "yes" && (
        <NumberField name="contingent_amount" label="Contingent amount (£) (1.72)" value={v.contingent_amount} onChange={(x) => set("contingent_amount", x)} />
      )}
      <NumberField name="chargeable_consideration" label="Chargeable consideration (£)" required hint="Basis for the SDLT calculation" value={v.chargeable_consideration} onChange={(x) => set("chargeable_consideration", x)} error={e.chargeable_consideration} />
      <NumberField name="apportionment_land" label="Apportionment — Land (£) (1.69)" hint="Mixed use only" value={v.apportionment_land} onChange={(x) => set("apportionment_land", x)} />
      <NumberField name="apportionment_buildings" label="Apportionment — Buildings (£) (1.69)" value={v.apportionment_buildings} onChange={(x) => set("apportionment_buildings", x)} />
    </PanelGrid>
  );
}

export function SdltPanelReliefs({ v, set }) {
  return (
    <PanelGrid>
      <SelectField name="relief_code" label="Are you claiming relief? (1.9)" value={v.relief_code} onChange={(x) => set("relief_code", x)} options={RELIEF_CODES} span={2} />
      <NumberField name="relief_amount" label="Relief amount (£)" hint="If claiming a partial relief" value={v.relief_amount} onChange={(x) => set("relief_amount", x)} />
      <TextAreaField name="relief_details" label="Details of relief claim (1.10)" rows={2} hint="Property address, value, qualifying conditions" value={v.relief_details} onChange={(x) => set("relief_details", x)} />
      <YesNoField name="first_time_buyer" label="First-time buyer relief?" value={v.first_time_buyer} onChange={(x) => set("first_time_buyer", x)} />
      <YesNoField name="additional_property" label="Additional property surcharge (3%)?" value={v.additional_property} onChange={(x) => set("additional_property", x)} />
      <YesNoField name="non_uk_resident_surcharge" label="Non-UK resident surcharge (2%)?" value={v.non_uk_resident_surcharge} onChange={(x) => set("non_uk_resident_surcharge", x)} />
    </PanelGrid>
  );
}

export function SdltPanelLease({ v, set, e }) {
  return (
    <PanelGrid>
      <p className="col-span-2 text-xs text-slate-500 -mb-1">
        Only for the <strong>grant of a new lease</strong>. SDLT applies to the premium <em>and</em> separately to the Net Present Value of the rent — enter the term, start date and projected rent so the NPV can be calculated. (Transfers and assignments don't need this — their SDLT is on the purchase price only.)
      </p>
      <NumberField name="lease_term_years" label="Lease term (years)" required step="1" value={v.lease_term_years} onChange={(x) => set("lease_term_years", x)} error={e.lease_term_years} />
      <DateField name="lease_start_date" label="Lease start date" required value={v.lease_start_date} onChange={(x) => set("lease_start_date", x)} error={e.lease_start_date} />
      <NumberField name="lease_premium" label="Lease premium (£)" value={v.lease_premium} onChange={(x) => set("lease_premium", x)} />
      <NumberField name="lease_rent_y1" label="Annual rent — Year 1 (£)" value={v.lease_rent_y1} onChange={(x) => set("lease_rent_y1", x)} />
      <NumberField name="lease_rent_y2" label="Year 2 (£)" value={v.lease_rent_y2} onChange={(x) => set("lease_rent_y2", x)} />
      <NumberField name="lease_rent_y3" label="Year 3 (£)" value={v.lease_rent_y3} onChange={(x) => set("lease_rent_y3", x)} />
      <NumberField name="lease_rent_y4" label="Year 4 (£)" value={v.lease_rent_y4} onChange={(x) => set("lease_rent_y4", x)} />
      <NumberField name="lease_rent_y5" label="Year 5 (£)" value={v.lease_rent_y5} onChange={(x) => set("lease_rent_y5", x)} />
    </PanelGrid>
  );
}

export function SdltPanelAgent({ v, set }) {
  return (
    <PanelGrid>
      <YesNoField name="has_agent" label="I authorise my agent to handle correspondence on my behalf (Q61)" value={v.has_agent} onChange={(x) => set("has_agent", x)} />
      {v.has_agent === "yes" && (
        <>
          <YesNoField name="agent_handle_correspondence" label="Does the purchaser authorise the agent to handle correspondence? (Q61)" value={v.agent_handle_correspondence} onChange={(x) => set("agent_handle_correspondence", x)} />
          <SelectField name="sdlt5_certificate_to" label="To which address shall we send the SDLT5 certificate? (Q60)" value={v.sdlt5_certificate_to} onChange={(x) => set("sdlt5_certificate_to", x)} options={SDLT5_DESTINATIONS} />
          <TextField name="agent_firm_name" label="Agent's name (Q62)" hint="Max 28 characters" value={v.agent_firm_name} onChange={(x) => set("agent_firm_name", x)} span={2} />
          <TextField name="agent_reference" label="Agent's reference (Q65)" value={v.agent_reference} onChange={(x) => set("agent_reference", x)} />
          <TextField name="agent_address_1" label="Agent's address line 1 (Q63)" value={v.agent_address_1} onChange={(x) => set("agent_address_1", x)} span={2} />
          <TextField name="agent_address_2" label="Address line 2 (Q63)" value={v.agent_address_2} onChange={(x) => set("agent_address_2", x)} />
          <TextField name="agent_town" label="Town (Q63)" value={v.agent_town} onChange={(x) => set("agent_town", x)} />
          <TextField name="agent_postcode" label="Agent's postcode (Q63)" value={v.agent_postcode} onChange={(x) => set("agent_postcode", x)} />
          <TextField name="agent_phone" label="Agent's telephone number (Q66)" hint="Max 14 characters" value={v.agent_phone} onChange={(x) => set("agent_phone", x)} />
        </>
      )}
    </PanelGrid>
  );
}

export function SdltPanelTaxPayment({ v, set, e, caseId }) {
  const [calcOpen, setCalcOpen] = useState(false);
  const [autoCalcMsg, setAutoCalcMsg] = useState("");

  // Auto-calculate the SDLT on open when the total hasn't been entered yet,
  // so the solicitor doesn't have to re-key a figure the system can derive.
  // Uses the same HMRC engine as the Conveyancing Fees calculator.
  useEffect(() => {
    const pv = Number(v.chargeable_consideration || v.total_consideration || 0);
    const alreadyHasTotal = v.total_tax_due !== "" && v.total_tax_due != null;
    if (!caseId || alreadyHasTotal || !pv) return;
    let cancelled = false;
    (async () => {
      try {
        const params = {
          property_value: pv,
          first_time_buyer: v.first_time_buyer === "yes",
          second_home: v.additional_property === "yes",
          ltd_company: (v.purchasers || []).some((p) => p.type === "company"),
          commercial: v.property_type === "03",
          non_uk_resident: v.non_uk_resident_surcharge === "yes",
          corporate_body: (v.purchasers || []).some((p) => p.type === "company"),
        };
        const { data } = await API.post(`/cases/${caseId}/fees/sdlt/calculate`, params);
        if (cancelled || data == null || data.total == null) return;
        const total = Number(data.total);
        set("total_tax_due", total);
        if (v.amount_paid === "" || v.amount_paid == null) set("amount_paid", total);
        const lines = [];
        (data.bands || []).forEach((b) => {
          if (b.tax) lines.push(`£${Number(b.from).toLocaleString("en-GB")}–£${Number(b.to).toLocaleString("en-GB")} @ ${b.rate}% = £${Number(b.tax || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`);
        });
        if (lines.length && !v.tax_band_breakdown) set("tax_band_breakdown", lines.join("\n"));
        setAutoCalcMsg(`Auto-calculated from £${pv.toLocaleString("en-GB")} — review and adjust if needed.`);
      } catch (_) { /* silent — solicitor can use the Calculate button */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  const applyCalc = (total, params, calc) => {
    set("total_tax_due", total);
    if (!v.amount_paid) set("amount_paid", total);
    // Mirror the figure the calc was based on into the consideration
    // fields if the solicitor hasn't entered them yet.
    if (params.property_value) {
      if (!v.chargeable_consideration) set("chargeable_consideration", params.property_value);
      if (!v.total_consideration) set("total_consideration", params.property_value);
    }
    // Keep the surcharge/relief flags in step with the calculation.
    set("first_time_buyer", params.first_time_buyer ? "yes" : "no");
    set("additional_property", params.second_home ? "yes" : "no");
    set("non_uk_resident_surcharge", params.non_uk_resident ? "yes" : "no");
    // Build a human-readable band breakdown for Q82.
    const lines = [];
    (calc?.bands || []).forEach((b) => {
      lines.push(`£${Number(b.from).toLocaleString("en-GB")}–£${Number(b.to).toLocaleString("en-GB")} @ ${b.rate}% = £${Number(b.tax || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`);
    });
    (calc?.npv_bands || []).forEach((b) => {
      lines.push(`NPV £${Number(b.from).toLocaleString("en-GB")}–£${Number(b.to).toLocaleString("en-GB")} @ ${b.rate}% = £${Number(b.tax || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`);
    });
    if (lines.length) set("tax_band_breakdown", lines.join("\n"));
    setCalcOpen(false);
  };

  return (
    <PanelGrid>
      <div className="col-span-full flex items-center justify-between gap-2 bg-indigo-50/60 border border-indigo-100 rounded-lg px-3 py-2">
        <p className="text-[11px] text-slate-600 leading-snug">
          Work out the SDLT here — the same HMRC calculator used in Conveyancing Fees. The total, band breakdown and surcharge flags fill in automatically.
        </p>
        <Button type="button" variant="outline" size="sm" className="shrink-0 h-8 text-xs gap-1.5 border-indigo-300 text-indigo-700 hover:bg-indigo-50" onClick={() => setCalcOpen(true)} data-testid="sdlt-open-calculator-btn">
          <Calculator weight="bold" className="w-3.5 h-3.5" /> Calculate SDLT
        </Button>
      </div>
      <TextAreaField name="tax_band_breakdown" label="Tax band breakdown" rows={3} hint="e.g. 0% to £250k, 5% £250k-£925k, 10% £925k-£1.5m, 12% above" value={v.tax_band_breakdown} onChange={(x) => set("tax_band_breakdown", x)} />
      <NumberField name="total_tax_due" label="Total SDLT due (£) (Q14)" required value={v.total_tax_due} onChange={(x) => set("total_tax_due", x)} error={e.total_tax_due} />
      {autoCalcMsg && (
        <p className="col-span-full -mt-1 text-[11px] text-emerald-700" data-testid="sdlt-autocalc-note">{autoCalcMsg}</p>
      )}
      <NumberField name="amount_paid" label="Amount paid / enclosed (£) (Q15)" required value={v.amount_paid} onChange={(x) => set("amount_paid", x)} error={e.amount_paid} />
      <YesNoField name="amount_paid_includes_penalties" label="Amount paid includes penalties?" value={v.amount_paid_includes_penalties} onChange={(x) => set("amount_paid_includes_penalties", x)} />
      <YesNoField name="tax_paid" label="Will the client pay the SDLT once the UTRN is issued?" value={v.tax_paid} onChange={(x) => set("tax_paid", x)} />
      {v.tax_paid === "yes" && (
        <SelectField name="payment_method" label="Intended payment method" value={v.payment_method} onChange={(x) => set("payment_method", x)} options={PAYMENT_METHODS} />
      )}
      <div className="col-span-full text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid="sdlt-utrn-note">
        HMRC issues the <strong>UTRN</strong> (Unique Transaction Reference Number) only <strong>after</strong> this return is submitted. It's shown on the submission receipt and is used as the payment reference when paying the SDLT — it cannot be entered here.
      </div>
      <SdltCalculatorDialog
        open={calcOpen}
        onClose={() => setCalcOpen(false)}
        caseId={caseId}
        seedValue={Number(v.chargeable_consideration || v.total_consideration || 0) || 0}
        sdltParams={{
          property_value: Number(v.chargeable_consideration || v.total_consideration || 0) || 0,
          first_time_buyer: v.first_time_buyer === "yes",
          second_home: v.additional_property === "yes",
          non_uk_resident: v.non_uk_resident_surcharge === "yes",
        }}
        onApplied={applyCalc}
      />
    </PanelGrid>
  );
}

export function SdltPanelDeclaration({ v, set, e }) {
  return (
    <PanelGrid>
      <SelectField name="declaration_type" label="Appropriate declaration" required value={v.declaration_type} onChange={(x) => set("declaration_type", x)} options={DECLARATION_OPTIONS} span={3} error={e.declaration_type} />
      <div className="sm:col-span-3 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-3 leading-relaxed" data-testid="declaration-text">
        {DECLARATION_TEXT[v.declaration_type] || ""}
      </div>
      <SelectField name="signing_capacity" label="Capacity of signer" value={v.signing_capacity} onChange={(x) => set("signing_capacity", x)} options={SIGN_CAPACITY} />
      <TextField name="signing_name" label="Name signing the declaration" required value={v.signing_name} onChange={(x) => set("signing_name", x)} error={e.signing_name} span={2} />
      <DateField name="signing_date" label="Date of signature" value={v.signing_date} onChange={(x) => set("signing_date", x)} />
    </PanelGrid>
  );
}

export function sdltPanels() {
  return [
    { id: "purchasers", label: "About the purchaser(s)", Component: SdltPanelPurchasers, fields: ["purchasers"], requiredFields: ["purchasers"] },
    { id: "vendors", label: "About the vendor(s)", Component: SdltPanelVendors, fields: ["vendors", "vendor_agent_name", "vendor_agent_address_1", "vendor_agent_postcode", "vendor_agent_reference", "vendor_agent_phone"], requiredFields: ["vendors"] },
    { id: "land", label: "About the land", Component: SdltPanelLand, fields: ["property_type", "interest_transferred", "property_address_1", "property_town", "property_postcode", "local_authority_code", "sending_plan_by_post", "title_number", "uprn"], requiredFields: ["property_type", "interest_transferred", "property_address_1", "property_town", "local_authority_code"] },
    { id: "residency", label: "About UK residency", Component: SdltPanelResidency, fields: ["any_purchaser_non_uk_resident", "crown_employment_relief"], requiredFields: [] },
    { id: "transaction", label: "About the transaction", Component: SdltPanelTransaction, fields: ["transaction_description", "effective_date", "contract_date"], requiredFields: ["transaction_description", "effective_date"] },
    { id: "consideration", label: "Consideration & value", Component: SdltPanelConsideration, fields: ["total_consideration", "chargeable_consideration", "consideration_form"], requiredFields: ["total_consideration", "chargeable_consideration"] },
    { id: "reliefs", label: "Reliefs & exemptions", Component: SdltPanelReliefs, fields: ["relief_code", "first_time_buyer", "additional_property", "non_uk_resident_surcharge"], requiredFields: [] },
    { id: "lease", label: "New lease details", Component: SdltPanelLease, onlyIf: (v) => v.transaction_description === "L", fields: ["lease_term_years", "lease_start_date", "lease_premium"], requiredFields: ["lease_term_years", "lease_start_date"] },
    { id: "agent", label: "Filing agent details", Component: SdltPanelAgent, fields: ["has_agent", "agent_firm_name", "agent_reference", "agent_address_1", "agent_town", "agent_postcode", "agent_phone"], requiredFields: [] },
    { id: "tax", label: "Tax calculation & payment", Component: SdltPanelTaxPayment, fields: ["total_tax_due", "amount_paid", "amount_paid_includes_penalties", "tax_paid", "payment_method"], requiredFields: ["total_tax_due", "amount_paid"] },
    { id: "declaration", label: "Declaration & signature", Component: SdltPanelDeclaration, fields: ["signing_name", "signing_date", "signing_capacity"], requiredFields: ["signing_name"] },
  ];
}
